import { applyReviewedValues } from '../../../backend/src/services/ncfJobChange.mjs';
import { cleanPhoneValue } from '../../../backend/src/services/ncfFieldValidation.mjs';

const pathParts = (path) => String(path || '').replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);

export const getPath = (source, path) => pathParts(path).reduce((value, key) => value?.[key], source) ?? '';

export const setPath = (source, path, value) => {
  const keys = pathParts(path);
  let target = source;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) target[key] = value;
    else {
      const nextKey = keys[index + 1];
      target[key] = target[key] || (/^\d+$/.test(nextKey) ? [] : {});
      target = target[key];
    }
  });
};

export const applyNonEmptyPaths = (target, source) => {
  if (!source || typeof source !== 'object') return target;
  const visit = (value, parts) => {
    if (Array.isArray(value)) {
      if (value.length > 0) setPath(target, parts.join('.'), value);
      return;
    }
    if (value && typeof value === 'object') {
      Object.entries(value).forEach(([key, child]) => visit(child, [...parts, key]));
      return;
    }
    if (value != null && String(value).trim() !== '') {
      setPath(target, parts.join('.'), value);
    }
  };
  Object.entries(source).forEach(([key, value]) => visit(value, [key]));
  return target;
};

const isBlankValue = value => value == null || String(value).trim() === '';

const toDateOnlyValue = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
};

// Mirrors the backend: stored submission metadata wins, and empty stored values fall back to
// stable defaults instead of blanking the fields or drifting the effective date to today.
export const applySubmissionMetadataDefaults = (root, ncf) => {
  if (isBlankValue(getPath(root, 'ncf.submittedDate'))) setPath(root, 'ncf.submittedDate', ncf.createdAt || ncf.CreatedAt || null);
  if (isBlankValue(getPath(root, 'ncf.submittedBy'))) setPath(root, 'ncf.submittedBy', ncf.submittedByName || ncf.ownerName || null);
  if (isBlankValue(getPath(root, 'ncf.effectiveDate'))) {
    setPath(root, 'ncf.effectiveDate', toDateOnlyValue(getPath(ncf.reviewedData, 'ncf.submittedDate')) || new Date().toISOString().slice(0, 10));
  }
  return root;
};

// NCFs saved before phone cleanup kept JobDiva labels ("Work Phone: 2125550100"); clean imported
// phone values so they display and validate as plain numbers.
const PHONE_KEY = /phone|mobile/i;
const PHONE_LABEL = /^[^\d+(]*:|\([a-z]+\)\s*\|?\s*$|\|/i;
const cleanImportedPhones = (value) => {
  if (Array.isArray(value)) return value.forEach(cleanImportedPhones);
  if (!value || typeof value !== 'object') return;
  Object.entries(value).forEach(([key, child]) => {
    if (key === 'raw') return;
    if (typeof child === 'string' && PHONE_KEY.test(key) && PHONE_LABEL.test(child)) value[key] = cleanPhoneValue(child) || '';
    else cleanImportedPhones(child);
  });
};

export const buildNcfReviewRoot = (ncf, reviewData) => {
  const persistedRoot = {
    ncf: {},
    assignment: {
      payRate: ncf.payRate ?? ncf.PayRate ?? null,
      payRateType: ncf.payRateType || ncf.PayRateType || null,
      desiredStartDate: ncf.desiredStartDate || ncf.DesiredStartDate || null,
      clientName: ncf.clientName || ncf.ClientName || null,
      jobTitle: ncf.jobDivaJobTitle || ncf.JobDivaJobTitle || null,
      jobReference: ncf.jobDivaData?.job?.reference || null,
      jobLocation: ncf.jobDivaData?.job?.location || null,
      jobStartDate: ncf.jobDivaData?.job?.startDate || null,
      jobEndDate: ncf.jobDivaData?.job?.endDate || null
    },
    jobdiva: JSON.parse(JSON.stringify(ncf.jobDivaData || ncf.JobDivaData || {}))
  };
  cleanImportedPhones(persistedRoot.jobdiva);
  return applySubmissionMetadataDefaults(applyReviewedValues(applyNonEmptyPaths(persistedRoot, ncf.extractedData), reviewData), ncf);
};

export const NCF_CHANGE_STATUSES = ['submitted', 'approved', 'onboarded'];

export const C2C_OWNERSHIP_PATH = 'jobdiva.job.c2cCorporationOwned';
export const C2C_LINKED_NAME_PATH = 'jobdiva.job.c2cLinkedCorporationName';

const parseOwnership = (value) => {
  if (value === true || value === false) return value;
  const text = String(value ?? '').trim().toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(text)) return true;
  if (['no', 'n', 'false', '0'].includes(text)) return false;
  return null;
};

// When ownership is answered, prefill the matching corp-name field with the corporation linked
// from JobDiva, without overwriting a name the user already entered.
export const linkedCorporationPatch = (root, patch) => {
  if (!Object.prototype.hasOwnProperty.call(patch || {}, C2C_OWNERSHIP_PATH)) return {};
  const linkedName = String(getPath(root, C2C_LINKED_NAME_PATH) || '').trim();
  const ownership = parseOwnership(patch[C2C_OWNERSHIP_PATH]);
  if (!linkedName || ownership == null) return {};
  const target = ownership ? 'jobdiva.job.c2cCorporationName' : 'jobdiva.job.thirdPartyCorporationName';
  const current = Object.prototype.hasOwnProperty.call(patch, target) ? patch[target] : getPath(root, target);
  return isBlankValue(current) ? { [target]: linkedName } : {};
};
